import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CandidateSummary } from '@recruitai/shared';
import { getScoreColor, getNextStatus, canReject } from './kanban-utils';
import { ChevronRight, X, Mail } from 'lucide-react';

const SOURCE_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  career_page: { label: 'Candidatura Espontânea', variant: 'default' },
  recruiter_upload: { label: 'Upload Manual', variant: 'secondary' },
  batch_upload: { label: 'Lote', variant: 'outline' },
};

export interface CandidateCardProps {
  candidate: CandidateSummary;
  isReadOnly: boolean;
  isDragging?: boolean;
  onCandidateClick: (candidateId: string) => void;
  onQuickReject: (candidateId: string) => void;
  onQuickAdvance: (candidateId: string) => void;
}

export function CandidateCard({
  candidate,
  isReadOnly,
  onCandidateClick,
  onQuickReject,
  onQuickAdvance,
}: CandidateCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: candidate.candidateId,
    data: { status: candidate.status },
    disabled: isReadOnly,
  });

  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;
  const nextStatus = getNextStatus(candidate.status);
  const showReject = canReject(candidate.status);
  const scoreColor = getScoreColor(candidate.fitScore);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`p-3 mb-2 cursor-pointer transition-shadow hover:shadow-md ${
        isDragging ? 'opacity-50 shadow-lg' : ''
      }`}
      onClick={() => onCandidateClick(candidate.candidateId)}
    >
      <div className="flex justify-between items-start mb-1">
        <p className="font-medium text-sm truncate flex-1">{candidate.name}</p>
        <span className={`text-sm font-bold ml-2 ${scoreColor}`}>
          {candidate.fitScore != null ? `${candidate.fitScore}%` : 'Sem score'}
        </span>
      </div>

      {candidate.email && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2 truncate">
          <Mail className="h-3 w-3 shrink-0" />
          <span className="truncate">{candidate.email}</span>
        </div>
      )}

      {candidate.source && SOURCE_LABELS[candidate.source] && (
        <div className="mb-2">
          <Badge variant={SOURCE_LABELS[candidate.source].variant} className="text-[10px] px-1.5 py-0">
            {SOURCE_LABELS[candidate.source].label}
          </Badge>
        </div>
      )}

      {!isReadOnly && (nextStatus || showReject) && (
        <div className="flex gap-1 mt-1">
          {nextStatus && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-xs px-2"
              onClick={(e) => { e.stopPropagation(); onQuickAdvance(candidate.candidateId); }}
            >
              <ChevronRight className="h-3 w-3 mr-1" /> Avançar
            </Button>
          )}
          {showReject && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-xs px-2 text-destructive hover:bg-destructive/10"
              onClick={(e) => { e.stopPropagation(); onQuickReject(candidate.candidateId); }}
            >
              <X className="h-3 w-3 mr-1" /> Rejeitar
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
